// ProductListUK.js - Complete with linked (cascading) filters, grid table, 10 per page pagination, Stock inline, View modal, Edit page nav, City-specific delete, State Persistence & Bulk Stock

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaSearch, FaEye, FaEdit, FaTrash, FaPlus, FaMinus, FaTimes, FaClipboardList, FaCity, FaChevronDown, FaCheck } from "react-icons/fa";
import { useCurrency } from "../../context/CurrencyContext";
import TablePagination from "../../components/TablePagination";
import "./ProductList.css";

const API = "https://super-backend.hira-tech.co.uk/api/product";
const ORIGIN = "https://super-backend.hira-tech.co.uk";
const NO_IMAGE = "https://via.placeholder.com/70x70?text=No+Image";

// Har page par sirf itni rows
const PAGE_SIZE = 10;

// ✅ STATE PERSISTENCE HELPERS
const STATE_PREFIX = "productlist_state_";
const readSavedState = (id) => {
  try { return JSON.parse(sessionStorage.getItem(`${STATE_PREFIX}${id}`)) || {}; }
  catch { return {}; }
};

const PRODUCTLIST_CACHE_PREFIX = "productlist_cache_";

const readProductListCache = (id) => {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(`${PRODUCTLIST_CACHE_PREFIX}${id}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeProductListCache = (id, data) => {
  if (!id) return;
  try {
    localStorage.setItem(`${PRODUCTLIST_CACHE_PREFIX}${id}`, JSON.stringify(data));
  } catch (e) {}
};

const arr = (d) =>
  Array.isArray(d) ? d : Array.isArray(d?.results) ? d.results : Array.isArray(d?.data) ? d.data : [];

const pid = (p) => p.product_Id ?? p.id ?? p.product_id;

const label = (value, keys) =>
  typeof value === "string"
    ? value
    : value
    ? keys.map((key) => value[key]).find(Boolean) || ""
    : "";

const getCity = (stock) => stock.city ?? stock.selected_city ?? "";
const getStock = (stock) => Number(stock.stock ?? stock.product_Stock ?? 0);

const ALL_TYPES = "All Types";
const ALL_CATEGORIES = "All Categories";
const ALL_VARIATIONS = "All Variations";
const ALL_COMPANIES = "All Companies";
const ALL_BRANDS = "All Brands";

const matchesDimensions = (product, f, skip) =>
  (skip === "type" || f.type === ALL_TYPES || product.type === f.type) &&
  (skip === "category" || f.category === ALL_CATEGORIES || product.category === f.category) &&
  (skip === "variation" || f.variation === ALL_VARIATIONS || product.variation === f.variation) &&
  (skip === "company" || f.company === ALL_COMPANIES || product.company === f.company) &&
  (skip === "brand" || f.brand === ALL_BRANDS || product.brand === f.brand);

function ProductList() {
  const location = useLocation();
  const navigate = useNavigate();
  const { formatPrice, currency, loading: currencyLoading } = useCurrency();

  const initialShopId = location.pathname.match(/\/shop\/(\d+)/)?.[1] || "";

  // ✅ Read saved state
  const saved = useRef(readSavedState(initialShopId)).current;

  const [shopId, setShopId] = useState(initialShopId);
  const [shopName, setShopName] = useState("");
  const [cities, setCities] = useState([]);
  const [products, setProducts] = useState(() => readProductListCache(initialShopId) || []);
  const [loading, setLoading] = useState(() => !readProductListCache(initialShopId));
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  // ✅ Use saved state in useState
  const [search, setSearch] = useState(saved.search ?? "");
  const [city, setCity] = useState(saved.city ?? "All Cities");
  const [type, setType] = useState(saved.type ?? ALL_TYPES);
  const [category, setCategory] = useState(saved.category ?? ALL_CATEGORIES);
  const [variation, setVariation] = useState(saved.variation ?? ALL_VARIATIONS);
  const [company, setCompany] = useState(saved.company ?? ALL_COMPANIES);
  const [brand, setBrand] = useState(saved.brand ?? ALL_BRANDS);
  const [status, setStatus] = useState(saved.status ?? "All Stock Status");
  const [page, setPage] = useState(saved.page || 1);

  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityDropdownRef = useRef(null);

  const productsRequestRef = useRef(0);

  // ✅ Bulk Price & Bulk Stock States
  const [price, setPrice] = useState("");
  const [selected, setSelected] = useState([]);
  const [stockQty, setStockQty] = useState("");
  const [stockMode, setStockMode] = useState("add");
  const [stockBusy, setStockBusy] = useState(false);

  const [image, setImage] = useState("");
  const [viewProduct, setViewProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCity, setDeleteCity] = useState("");

  const notify = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 3200);
  };

  const stockStatus = (stock) =>
    stock <= 0 ? "Out of Stock" : stock <= 10 ? "Low Stock" : "In Stock";

  const imageUrl = (path) =>
    !path ? NO_IMAGE : /^https?:\/\//i.test(path) ? path : `${ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;

  // ✅ Save state on changes
  useEffect(() => {
    if (!shopId) return;
    try {
      sessionStorage.setItem(`${STATE_PREFIX}${shopId}`, JSON.stringify({
        search, city, type, category, variation, company, brand, status, page
      }));
    } catch (e) {}
  }, [shopId, search, city, type, category, variation, company, brand, status, page]);

  useEffect(() => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    if (match?.[1] && match[1] !== shopId) {
      const cached = readProductListCache(match[1]);
      setProducts(cached || []);
      setLoading(!cached);
      setShopId(match[1]);
    }
  }, [location.pathname, shopId]);

  useEffect(() => {
    if (shopId) return;
    fetch(`${API}/shops/`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => {
        const shop = arr(data)[0];
        if (shop) {
          const id = shop.shop_id ?? shop.id;
          setShopName(shop.shop_name ?? shop.name ?? `Shop ${id}`);
          setShopId(String(id));
          navigate(`/shop/${id}/productlist`, { replace: true });
        }
      })
      .catch(() => setError("Could not load shops. Check the API connection."));
  }, [shopId, navigate]);

  useEffect(() => {
    const handler = (e) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(e.target)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  async function loadCities(id) {
    try {
      const response = await fetch(`${API}/shop-cities/?shop_id=${id}`);
      if (!response.ok) throw new Error();
      const data = await response.json();
      const cityList = Array.isArray(data?.cities)
        ? data.cities
        : arr(data).map((item) => typeof item === "string" ? item : item.city).filter(Boolean);
      setCities([...new Set(cityList)]);
    } catch {
      setCities([]);
    }
  }

  async function loadProducts() {
    if (!shopId) return;
    const requestId = ++productsRequestRef.current;

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({ shop_id: shopId });
      if (city !== "All Cities") params.set("city", city);

      const response = await fetch(`${API}/products/?${params.toString()}`);
      if (requestId !== productsRequestRef.current) return;
      if (!response.ok) throw new Error(`Products request failed (${response.status}).`);
      const data = arr(await response.json());
      if (requestId !== productsRequestRef.current) return;

      const transformed = data.map((product) => {
        const gallery = product.imageGallery || product.image_gallery || {};
        const mainImage =
          product.main_image_url ||
          gallery.image1_url || gallery.image1 ||
          gallery.image2_url || gallery.image2 ||
          gallery.image3_url || gallery.image3 ||
          gallery.image4_url || gallery.image4;

        const cityStocks = Array.isArray(product.city_stocks)
          ? product.city_stocks.map((s) => ({ city: getCity(s), stock: getStock(s) }))
          : [];

        if (!cityStocks.length && getCity(product)) {
          cityStocks.push({ city: getCity(product), stock: getStock(product) });
        }

        const totalStock = cityStocks.length
          ? cityStocks.reduce((t, s) => t + s.stock, 0)
          : getStock(product);

        return {
          raw: product,
          id: pid(product),
          name: product.product_Name ?? product.name ?? "",
          sku: product.SKU ?? product.sku ?? "",
          image: imageUrl(mainImage),
          category: label(product.product_category, ["category_Name", "category_name"]) || "Uncategorized",
          type: label(product.type, ["type_name"]),
          variation: label(product.product_variation, ["variation_name"]),
          company: label(product.implant_company, ["company_name"]),
          brand: label(product.product_brand, ["brand_name"]) || "No Brand",
          price: Number(product.product_Price ?? product.price ?? 0),
          city: getCity(product) || "N/A",
          cityStocks,
          stock: totalStock,
          description: product.product_Description ?? "",
          size: product.size ?? "",
          reference: product.reference ?? "",
          lot_number: product.lot_number ?? "",
          pack: product.product_isSale ?? "Yes",
          rawMaterial: product.rawMaterial ?? "",
          features: product.features ?? "",
        };
      });

      setProducts(transformed);
      if (city === "All Cities") writeProductListCache(shopId, transformed);
      // ✅ Page reset hata diya gaya hai (state save se handle ho raha hai)
    } catch (err) {
      if (requestId === productsRequestRef.current) {
        setError(err.message || "Could not load products.");
      }
    } finally {
      if (requestId === productsRequestRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    if (shopId) {
      loadCities(shopId);
      loadProducts();
    }
  }, [shopId, city]);

  const options = useMemo(() => {
    const f = { type, category, variation, company, brand };
    const pick = (skip, key, exclude) =>
      [...new Set(
        products
          .filter((p) => matchesDimensions(p, f, skip))
          .map((p) => p[key])
          .filter((v) => v && v !== exclude)
      )].sort((a, b) => String(a).localeCompare(String(b)));

    return {
      types: pick("type", "type"),
      categories: pick("category", "category"),
      variations: pick("variation", "variation"),
      companies: pick("company", "company"),
      brands: pick("brand", "brand", "No Brand"),
    };
  }, [products, type, category, variation, company, brand]);

  useEffect(() => {
    if (!products.length) return;
    if (type !== ALL_TYPES && !options.types.includes(type)) setType(ALL_TYPES);
    if (category !== ALL_CATEGORIES && !options.categories.includes(category)) setCategory(ALL_CATEGORIES);
    if (variation !== ALL_VARIATIONS && !options.variations.includes(variation)) setVariation(ALL_VARIATIONS);
    if (company !== ALL_COMPANIES && !options.companies.includes(company)) setCompany(ALL_COMPANIES);
    if (brand !== ALL_BRANDS && !options.brands.includes(brand)) setBrand(ALL_BRANDS);
  }, [options, products.length, type, category, variation, company, brand]);

  const cityStock = (product) =>
    city === "All Cities"
      ? product.stock
      : product.cityStocks.find((s) => s.city === city)?.stock ?? 0;

  const filtered = products.filter((product) => {
    const query = search.toLowerCase().trim();
    return (
      (!query || product.name.toLowerCase().includes(query) || product.sku.toLowerCase().includes(query)) &&
      matchesDimensions(product, { type, category, variation, company, brand }, null) &&
      (status === "All Stock Status" || stockStatus(cityStock(product)) === status)
    );
  });

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ✅ Page clamp fix
  useEffect(() => {
    if (!loading && products.length && page > pages) {
      setPage(pages);
    }
  }, [page, pages, loading, products.length]);

  async function saveStock(product, newStock) {
    if (city === "All Cities") {
      notify("Choose a city first to change its stock.");
      return;
    }
    if (!Number.isInteger(newStock) || newStock < 0) {
      notify("Stock must be zero or a positive whole number.");
      return;
    }

    let response = await fetch(`${API}/products/${product.id}/city-stock/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ city, stock: newStock }),
    });

    if (response.status === 404 || response.status === 405) {
      response = await fetch(`${API}/products/${product.id}/update-city-stock/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ city, stock: newStock }),
      });
    }

    if (!response.ok) {
      notify("City-wise stock API is not available yet. No stock was changed.");
      return;
    }

    await loadProducts();
    notify(`${product.name} stock updated for ${city}.`);
  }

  async function bulkPrice() {
    if (!selected.length || price === "" || !Number.isFinite(Number(price))) {
      notify("Select products and enter a valid price.");
      return;
    }

    const response = await fetch(`${API}/products/bulk-price-update/`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_ids: selected, new_price: Number(price) }),
    });

    if (!response.ok) {
      notify("Price update failed.");
      return;
    }

    setPrice("");
    setSelected([]);
    await loadProducts();
    notify("Selected prices updated.");
  }

  // ✅ BULK STOCK UPDATE FUNCTION
  async function bulkStock() {
    if (!selected.length) { notify("Select products first."); return; }
    if (city === "All Cities") { notify("Choose a city first (top right) to change its stock."); return; }
    const qty = Number(stockQty);
    if (stockQty === "" || !Number.isInteger(qty) || qty < 0) {
      notify("Enter a valid whole number for stock.");
      return;
    }

    const jobs = [];
    let skipped = 0;
    products.filter((p) => selected.includes(p.id)).forEach((p) => {
      const row = p.cityStocks.find((s) => s.city === city);
      if (!row) { skipped += 1; return; }
      const next =
        stockMode === "add" ? row.stock + qty :
        stockMode === "sub" ? Math.max(0, row.stock - qty) : qty;
      const stocks = p.cityStocks.map((s) => ({ city: s.city, stock: s.city === city ? next : s.stock }));
      jobs.push({ p, stocks });
    });

    if (!jobs.length) { notify(`None of the selected products has stock in ${city}.`); return; }

    const label = stockMode === "add" ? `add ${qty} to` : stockMode === "sub" ? `subtract ${qty} from` : `set to ${qty}`;
    if (!window.confirm(`${label} the ${city} stock of ${jobs.length} product(s)?`)) return;

    setStockBusy(true);
    let ok = 0, failed = 0;
    const CHUNK = 5;

    for (let i = 0; i < jobs.length; i += CHUNK) {
      const chunk = jobs.slice(i, i + CHUNK);
      const results = await Promise.allSettled(chunk.map(({ p, stocks }) => {
        const raw = p.raw;
        const fd = new FormData();
        fd.append("product_Name", raw.product_Name || "");
        fd.append("product_Description", raw.product_Description || "");
        fd.append("product_Price", raw.product_Price || 0);
        fd.append("SKU", raw.SKU || "");
        fd.append("shop_id", shopId);
        fd.append("product_isNew", false);
        fd.append("city_stocks", JSON.stringify(stocks));
        fd.append("city", stocks[0].city);
        fd.append("product_Stock", stocks.reduce((s, c) => s + c.stock, 0));
        return fetch(`${API}/products/${p.id}/update/`, { method: "PUT", body: fd })
          .then((r) => { if (!r.ok) throw new Error(); });
      }));
      results.forEach((r) => (r.status === "fulfilled" ? ok++ : failed++));
    }

    setStockBusy(false);
    setStockQty("");
    setSelected([]);
    await loadProducts();
    notify(`Stock updated for ${ok} product(s)${failed ? `, ${failed} failed` : ""}${skipped ? `, ${skipped} skipped (no ${city} stock)` : ""}.`);
  }

  async function deleteCityStock() {
    if (!deleteTarget) return;

    if (deleteCity === "__ALL__" || deleteCity === "") {
      if (!deleteCity) {
        notify("Please select an option.");
        return;
      }
      try {
        const response = await fetch(`${API}/products/${deleteTarget.id}/delete/`, { method: "DELETE" });
        if (!response.ok) {
          notify("Failed to delete product.");
          return;
        }
        const deletedName = deleteTarget.name;
        setDeleteTarget(null);
        setDeleteCity("");
        await loadProducts();
        notify(`Product "${deletedName}" deleted completely.`);
      } catch {
        notify("Failed to delete product.");
      }
      return;
    }

    let response = await fetch(
      `${API}/products/${deleteTarget.id}/city-stock/?city=${encodeURIComponent(deleteCity)}`,
      { method: "DELETE" }
    );

    if (response.status === 404 || response.status === 405) {
      response = await fetch(
        `${API}/products/${deleteTarget.id}/delete-city-stock/?city=${encodeURIComponent(deleteCity)}`,
        { method: "DELETE" }
      );
    }

    if (!response.ok) {
      notify("City-specific delete API is not available. Product was not deleted.");
      return;
    }

    const deletedName = deleteTarget.name;
    const deletedCity = deleteCity;
    setDeleteTarget(null);
    setDeleteCity("");
    await loadProducts();
    notify(`Deleted ${deletedName} stock for ${deletedCity}.`);
  }

  const handleEditProduct = (product) => {
    // ✅ Save product ID for scrolling later (if going to grid)
    sessionStorage.setItem('products_last_edited', String(product.id));
    navigate(`/shop/${shopId}/productlist/edit/${product.id}`, {
      state: { product: product.raw, shopId, shopName, from: "productlist" },
    });
  };

  const clearFilters = () => {
    setSearch("");
    setType(ALL_TYPES);
    setCategory(ALL_CATEGORIES);
    setVariation(ALL_VARIATIONS);
    setCompany(ALL_COMPANIES);
    setBrand(ALL_BRANDS);
    setStatus("All Stock Status");
    setCity("All Cities");
    setPage(1);
  };

  const onFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  return (
    <div className="products-management">
      {toast && (
        <div className="product-inline-toast">
          {toast}
          <button onClick={() => setToast("")}>×</button>
        </div>
      )}

      <div className="products-action-section">
        <div className="products-heading">
          <h2>
            <FaClipboardList className="products-heading-icon" />
            Products List
            {shopName && <span className="products-shop-name">— {shopName}</span>}
          </h2>
        </div>
        <div className="products-action-right">

          <div className="city-filter-inline" ref={cityDropdownRef}>
            <button
              type="button"
              className={`city-filter-inline-btn ${city && city !== "All Cities" ? "active" : ""}`}
              onClick={() => setShowCityDropdown(!showCityDropdown)}
            >

              <span>{city && city !== "All Cities" ? city : "All Cities"}</span>
              <FaChevronDown className={`city-filter-inline-arrow ${showCityDropdown ? "rotated" : ""}`} />
            </button>

            {showCityDropdown && (
              <div className="city-filter-inline-dropdown">
                <button
                  type="button"
                  className={`city-filter-inline-item ${city === "All Cities" ? "selected" : ""}`}
                  onClick={() => { setCity("All Cities"); setShowCityDropdown(false); setPage(1); }}
                >
                  <span>All Cities</span>
                  {city === "All Cities" && <FaCheck />}
                </button>
                {cities.map((c, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`city-filter-inline-item ${city === c ? "selected" : ""}`}
                    onClick={() => { setCity(c); setShowCityDropdown(false); setPage(1); }}
                  >
                    <span>{c}</span>
                    {city === c && <FaCheck />}
                  </button>
                ))}
                {cities.length === 0 && <div className="city-filter-inline-empty">No cities available</div>}
              </div>
            )}
          </div>

          <div className="products-search-wrapper">
            <FaSearch className="products-search-icon" />
            <input
              className="products-search-input"
              value={search}
              placeholder="Search products..."
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>
      </div>

      <div className="filters-section">
        <div className="filters-label">Filters:</div>
        <div className="filters-row">
          <select value={type} onChange={onFilterChange(setType)}>
            <option>{ALL_TYPES}</option>
            {options.types.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select value={category} onChange={onFilterChange(setCategory)}>
            <option>{ALL_CATEGORIES}</option>
            {options.categories.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select value={variation} onChange={onFilterChange(setVariation)}>
            <option>{ALL_VARIATIONS}</option>
            {options.variations.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select value={company} onChange={onFilterChange(setCompany)}>
            <option>{ALL_COMPANIES}</option>
            {options.companies.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select value={brand} onChange={onFilterChange(setBrand)}>
            <option>{ALL_BRANDS}</option>
            {options.brands.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select value={status} onChange={onFilterChange(setStatus)}>
            <option>All Stock Status</option>
            <option>In Stock</option>
            <option>Low Stock</option>
            <option>Out of Stock</option>
          </select>
        </div>
        <div className="clear-all-btn-container">
          <button className="clear-filters-btn" onClick={clearFilters}>Clear All</button>
          {city !== "All Cities" && <span className="active-city-label">City: {city}</span>}
        </div>
      </div>

      {/* ✅ UPDATED: Bulk Price & Bulk Stock Section */}
      <div className="price-update-section">
        <input
          type="number"
          min="0"
          className="price-input"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder={`New price (${currency})`}
        />
        <button className="update-prices-btn" onClick={bulkPrice} disabled={!selected.length}>
          Update Selected Prices ({selected.length})
        </button>

        <span style={{ margin: "0 8px", color: "#94a3b8" }}>|</span>

        <select className="price-input" style={{ width: 130 }} value={stockMode} onChange={(e) => setStockMode(e.target.value)}>
          <option value="add">Add stock (+)</option>
          <option value="sub">Subtract (−)</option>
          <option value="set">Set stock to</option>
        </select>
        <input
          type="number"
          min="0"
          className="price-input"
          value={stockQty}
          onChange={(e) => setStockQty(e.target.value)}
          placeholder={city === "All Cities" ? "Select a city first" : `Stock (${city})`}
        />
        <button className="update-prices-btn" onClick={bulkStock} disabled={!selected.length || stockBusy}>
          {stockBusy ? "Updating..." : `Update Selected Stock (${selected.length})`}
        </button>
      </div>

      {error && <div className="products-error">{error}</div>}

      <div className="products-table-container">
        <div className="products-table-wrapper">
          <table className="products-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={current.length > 0 && current.every((p) => selected.includes(p.id))}
                    onChange={() =>
                      setSelected((old) =>
                        current.every((p) => old.includes(p.id))
                          ? old.filter((id) => !current.some((p) => p.id === id))
                          : [...new Set([...old, ...current.map((p) => p.id)])]
                      )
                    }
                  />
                </th>
                <th>IMAGE</th>
                <th>PRODUCT NAME</th>
                <th>SKU</th>
                <th>CATEGORY</th>
                <th>BRAND</th>
                <th>PRICE ({currency})</th>
                <th>STOCK & CITY</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {current.length ? (
                current.map((product) => {
                  const stock = cityStock(product);
                  return (
                    <tr key={product.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.includes(product.id)}
                          onChange={() =>
                            setSelected((old) =>
                              old.includes(product.id)
                                ? old.filter((id) => id !== product.id)
                                : [...old, product.id]
                            )
                          }
                        />
                      </td>
                      <td>
                        <img
                          src={product.image}
                          alt={product.name}
                          className="product-list-image"
                          onClick={() => setImage(product.image)}
                          onError={(e) => { e.currentTarget.src = NO_IMAGE; }}
                        />
                      </td>
                      <td><span className="products-name">{product.name}</span></td>
                      <td>{product.sku || "-"}</td>
                      <td>{product.category}</td>
                      <td>{product.brand}</td>
                      <td className="products-price-cell">{formatPrice(product.price)}</td>

                      <td>
                        <div className="stock-city-block">
                          {product.cityStocks.length > 0 ? (
                            product.cityStocks.map((cs, i) => (
                              <div key={i} className="stock-city-row">
                                <span className="stock-city-name">{cs.city}</span>
                                <span className="stock-city-qty">{cs.stock}</span>
                                {city !== "All Cities" && cs.city === city && (
                                  <span className="stock-city-actions">
                                    <button
                                      title="Decrease"
                                      disabled={cs.stock <= 0}
                                      onClick={() => saveStock(product, cs.stock - 1)}
                                    ><FaMinus /></button>
                                    <button
                                      title="Increase"
                                      onClick={() => saveStock(product, cs.stock + 1)}
                                    ><FaPlus /></button>
                                  </span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="stock-city-row">
                              <span className="stock-city-name">-</span>
                              <span className="stock-city-qty">{stock}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      <td>
                        <span className={`status-badge ${stockStatus(stock).toLowerCase().replace(/\s+/g, "-")}`}>
                          {stockStatus(stock)}
                        </span>
                      </td>

                      <td>
                        <div className="product-row-actions">
                          <button title="View" onClick={() => setViewProduct(product)}>
                            <FaEye />
                          </button>
                          <button title="Edit" onClick={() => handleEditProduct(product)}>
                            <FaEdit />
                          </button>
                          <button
                            title="Delete"
                            onClick={() => {
                              setDeleteTarget(product);
                              setDeleteCity(city === "All Cities" ? "" : city);
                            }}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="no-data-cell">
                    {loading ? "" : "No products found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <TablePagination
        page={page}
        totalItems={filtered.length}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      {image && (
        <div className="image-modal-overlay" onClick={() => setImage("")}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={() => setImage("")}>×</button>
            <img className="image-modal-img" src={image} alt="Product" />
          </div>
        </div>
      )}

      {viewProduct && (
        <div className="product-modal-overlay" onClick={() => setViewProduct(null)}>
          <div className="product-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="product-view-header">
              <h3>{viewProduct.name}</h3>
              <button className="view-modal-close" onClick={() => setViewProduct(null)}><FaTimes /></button>
            </div>

            <div className="product-view-body">
              <div className="product-view-image">
                <img src={viewProduct.image} alt={viewProduct.name} onError={(e) => { e.currentTarget.src = NO_IMAGE; }} />
              </div>

              <div className="product-view-details">
                <div className="view-detail-row"><span className="view-label">SKU</span><span className="view-value">{viewProduct.sku || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Type</span><span className="view-value">{viewProduct.type || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Category</span><span className="view-value">{viewProduct.category || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Variation</span><span className="view-value">{viewProduct.variation || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Implant Company</span><span className="view-value">{viewProduct.company || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Brand</span><span className="view-value">{viewProduct.brand || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Reference</span><span className="view-value">{viewProduct.reference || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Price</span><span className="view-value price">{formatPrice(viewProduct.price)}</span></div>
                <div className="view-detail-row"><span className="view-label">Raw Material</span><span className="view-value">{viewProduct.rawMaterial || "-"}</span></div>
                <div className="view-detail-row"><span className="view-label">Features</span><span className="view-value">{viewProduct.features || "-"}</span></div>

                <div className="view-detail-row view-stock-row">
                  <span className="view-label">Stock by City</span>
                  <div className="view-stock-list">
                    {viewProduct.cityStocks.length > 0 ? (
                      viewProduct.cityStocks.map((cs, i) => (
                        <div key={i} className="view-stock-item">
                          <span className="stock-city-name">{cs.city}</span>
                          <span className="stock-city-qty">{cs.stock} units</span>
                        </div>
                      ))
                    ) : <span className="view-value">-</span>}
                  </div>
                </div>

                {viewProduct.description && (
                  <div className="view-detail-row view-desc-row">
                    <span className="view-label">Description</span>
                    <span className="view-value">{viewProduct.description}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="product-view-footer">
              <button className="secondary-btn" onClick={() => setViewProduct(null)}>Close</button>
              <button className="primary-btn" onClick={() => { setViewProduct(null); handleEditProduct(viewProduct); }}>
                <FaEdit /> Edit Product
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="product-modal-overlay" onClick={() => { setDeleteTarget(null); setDeleteCity(""); }}>
          <div className="product-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Product</h3>
            <p><strong>{deleteTarget.name}</strong></p>
            <p>Choose which city's stock you want to delete:</p>

            <select value={deleteCity} onChange={(e) => setDeleteCity(e.target.value)}>
              <option value="">-- Select an option --</option>
              {deleteTarget.cityStocks.map((cs, i) => (
                <option key={i} value={cs.city}>
                  {cs.city} ({cs.stock} units)
                </option>
              ))}
              <option value="__ALL__">🗑️ All Cities (Delete whole product)</option>
            </select>

            <div className="product-modal-actions">
              <button className="secondary-btn" onClick={() => { setDeleteTarget(null); setDeleteCity(""); }}>
                Cancel
              </button>
              <button className="danger-btn" disabled={!deleteCity} onClick={deleteCityStock}>
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductList;